import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import paths from 'app/paths'
import { useOrganization } from 'contexts/organization'
import { useAccess } from 'contexts/access'
import { useSmartEntity } from 'contexts/smart-entity'
import { useCheckDocumentPermission } from 'pages/documents/utilities'
import { useCategories } from 'pages/documents/utilities'
import { getContentSources } from 'components/tiptap/extensions/variable'
import { sum } from 'utilities/array'
import {
    Aside, Sections, Section,
    Kicker, CloseButton, FileNameIcon, FakeLabel
} from '../../s'
import { H4 } from 'components/typography/heading'
import Fields from './fields'
import { Field } from './fields/s'
import Form from 'components/form/controller'
import CheckboxField from 'components/form/field/checkbox'
import Link from 'components/link'
import { Label, ValueDisplay, Helper } from 'components/form/field/s'
import Clamp from 'components/clamp'
import WordList from 'components/word-list'
import Actions from './actions'
import Meta from 'components/meta'

const PreviewSmartDocumentDetailsEdit = ({
    listDokument,
    mode = 'view',
    viewContext = 'modal',
    slim = false,
    setIncludeLogo,
    previewAnother,
    modal,
    salt,
    ...props
}) => {
    const { formatMessage } = useIntl()

    const navigate = useNavigate()

    const { organization = {} } = useOrganization()

    const { checkFeature } = useAccess()
    const customLogoAccess = checkFeature('company-logo')

    const {
        entity: dokument,
        hasFetched
    } = useSmartEntity()

    const { editable } = useCheckDocumentPermission()(dokument)

    const [smartFieldsMeta, setSmartFieldsMeta] = useState(null)
    const [editing, setEditing] = useState(null)
    const [loading, setLoading] = useState(true)
    const [initialized, setInitialized] = useState(false)

    useEffect(() => {
        if(hasFetched && !initialized) {
            if(editable && mode === 'edit') {
                setEditing('auto')
            }

            setSmartFieldsMeta(getContentSources(dokument))
            setLoading(false)
            setInitialized(true)
        }
    }, [hasFetched, editable, mode, dokument, initialized])

    const {
        categories = [],
        getCategoryLabel
    } = useCategories()

    const NameAndCategory = ({ name, category }) => (
        <>
            <FileNameIcon
                name={name}
                extension="smart"
                ellipsify={false} />
            <FakeLabel>
                {getCategoryLabel(category ?? 'none')}
            </FakeLabel>
        </>
    )

    const MetaSection = ({ createdBy, createdAt }) => (
        <Section>
            <Meta
                type="created"
                by={createdBy}
                at={createdAt}
                time="fixed"
                className="compact" />
        </Section>
    )

    if(loading) {
        return (
            <Aside>
                <Sections>
                    <Section>
                        {viewContext === 'modal' && (
                            <Kicker>
                                <H4 className="compact">
                                    <FormattedMessage
                                        id="noun_details"
                                        defaultMessage="Details" />
                                </H4>
                                {<CloseButton onClick={modal.dismiss} />}
                            </Kicker>
                        )}
                        {!!listDokument && (
                            <>
                                <NameAndCategory {...listDokument} />
                                <Fields
                                    dokument={listDokument}
                                    editing={false}
                                    categories={categories}
                                    salt={salt} />
                            </>
                        )}
                    </Section>
                    <Section>
                        <Actions
                            {...props}
                            listDokument={listDokument}
                            slim={slim}
                            viewContext={viewContext}
                            setEditing={setEditing}
                            dismiss={viewContext === 'modal' ?
                                modal?.dismiss
                                : () => navigate(paths.documents.base)
                            }
                            salt={salt} />
                    </Section>
                    {!!listDokument && <MetaSection {...listDokument} />}
                </Sections>
            </Aside>
        )
    }

    return (
        <Aside>
            <Sections>
                <Section>
                    {viewContext === 'modal' && (
                        <Kicker>
                            <H4 className="compact">
                                <FormattedMessage
                                    id="noun_details"
                                    defaultMessage="Details" />
                            </H4>
                            {!editing && <CloseButton onClick={modal.dismiss} />}
                        </Kicker>
                    )}
                    {!editing && (
                        <>
                            <NameAndCategory {...dokument} />
                            {dokument.description && (
                                <Field>
                                    <Label>
                                        <FormattedMessage
                                            id="noun_description"
                                            defaultMessage="Description" />
                                    </Label>
                                    <ValueDisplay>
                                        <Clamp
                                            className="compact"
                                            lines={3}>
                                            {dokument.description}
                                        </Clamp>
                                    </ValueDisplay>
                                </Field>
                            )}
                        </>
                    )}
                    <Fields
                        dokument={dokument}
                        editing={editing}
                        setEditing={setEditing}
                        previewAnother={previewAnother}
                        categories={categories}
                        salt={salt} />
                </Section>
                {(!!smartFieldsMeta?.length && !editing) && (
                    <Section>
                        <Field className="compact">
                            <Label>
                                <FormattedMessage
                                    id="documents_smart_fields"
                                    defaultMessage="Smart fields" />
                            </Label>
                            <ValueDisplay>
                                <WordList
                                    words={[
                                        formatMessage({
                                            id: 'documents_smart_data_sources_count',
                                            defaultMessage: '{count, plural, one {1 data source} other {{count} data sources}}'
                                        }, { count: smartFieldsMeta.length }),
                                        formatMessage({
                                            id: 'documents_smart_variable_references_count',
                                            defaultMessage: '{count, plural, one {1 field reference} other {{count} field references}}'
                                        }, { count: sum(smartFieldsMeta.map(({ count }) => count)) })
                                    ]}
                                    className="compact" />
                            </ValueDisplay>
                        </Field>
                    </Section>
                )}
                {(!!customLogoAccess && !editing) && (
                    <Section>
                        <Form layout="vertical">
                            <CheckboxField
                                salt={salt}
                                className="compact"
                                name="logo"
                                field={{ value: !!organization?.companyLogo }}
                                label={formatMessage({
                                    id: 'documents_smart_template_label_include_logo',
                                    defaultMessage: 'Use logo as letterhead'
                                })}
                                interaction="switch"
                                enabled={!!organization?.companyLogo}
                                onChange={({ logo }) => setIncludeLogo(logo)} />
                            {!organization?.companyLogo && (
                                <Helper>
                                    <FormattedMessage
                                        id="documents_smart_template_helper_include_logo"
                                        defaultMessage="To use a logo, you must first upload one in the <link>System settings</link>"
                                        values={{
                                            link: chunks => (
                                                <Link
                                                    to={paths.settings.base}
                                                    target="_blank"
                                                    className="constructive">
                                                    {chunks}
                                                </Link>
                                            )
                                        }} />
                                </Helper>
                            )}
                        </Form>
                    </Section>
                )}
                {!editing && (
                    <>
                        <Section>
                            <Actions
                                {...props}
                                slim={slim}
                                viewContext={viewContext}
                                setEditing={setEditing}
                                dismiss={viewContext === 'modal' ?
                                    modal?.dismiss
                                    : () => navigate(paths.documents.base)
                                }
                                salt={salt} />
                        </Section>
                        <MetaSection {...dokument} />
                    </>
                )}
            </Sections>
        </Aside>
    )
}

export default PreviewSmartDocumentDetailsEdit